
import {  EXCEL_LAYOUT } from "./constants";
import apiClient, { getIdToken } from "./apiClient";

// Re-export getIdToken so existing imports from apiService still work
export { getIdToken };

// ============================================
// FORMULA CONVERSION UTILITIES
// ============================================

/**
 * Convert Excel column letter(s) to 0-based index
 */
function colLetterToIndex(letters: string): number {
  return letters.split("").reduce((acc, ch) => acc * 26 + (ch.charCodeAt(0) - 64), 0) - 1;
}

/**
 * Convert cell reference formula to structured reference formula
 * Example: "=I5+H5" -> "=[@[Ad Sales]]+[@[Ad Spend]]"
 */
function convertToStructuredReference(
  formula: string,
  columnNames: string[],
  currentRowIndex: number
): string {
  if (!formula || !formula.trim().startsWith("=")) {
    return formula;
  }

  console.log(`🔄 Converting formula: ${formula}`);
  console.log(
    `   Available columns (${columnNames.length}): ${columnNames.slice(0, 10).join(", ")}...`
  );
  console.log(`   Current row index: ${currentRowIndex}`);

  // Match cell references like I5, H5, AA10, etc.
  const cellRegex = /\b([A-Z]+)(\d+)\b/g;

  const convertedFormula = formula.replace(cellRegex, (match, colLetter, rowNum) => {
    const rowNumber = parseInt(rowNum, 10);

    // Only convert references that are on the same row as the formula
    if (rowNumber !== currentRowIndex) {
      console.log(`   ⚠️ Skipping ${match} - different row (${rowNumber} vs ${currentRowIndex})`);
      return match;
    }

    const colIndex = colLetterToIndex(colLetter);

    if (colIndex < 0 || colIndex >= columnNames.length) {
      console.log(`   ⚠️ Column ${colLetter} (index ${colIndex}) out of range`);
      return match;
    }

    const colName = columnNames[colIndex];

    if (!colName || colName.trim() === "") {
      console.log(`   ⚠️ No column name found for ${colLetter}`);
      return match;
    }

    const structuredRef = `[@[${colName}]]`;
    console.log(`   ✓ Converted ${match} -> ${structuredRef}`);
    return structuredRef;
  });

  console.log(`✅ Final formula: ${convertedFormula}`);
  return convertedFormula;
}

// ============================================
// MARKET WATCH API FUNCTIONS
// ============================================

export interface MarketWatchFilters {
  asins: string[];
  keywords: string[];
  asin_keyword_pairs: [string, string][];
  brands: string[];
  brand_asin_pairs: [string, string][];
}

export interface MarketWatchColumnSemantics {
  column_name: string;
  meaning: string;
}

export interface MarketWatchDataResponse {
  file_name: string;
  brand: string;
  target_asin: string;
  headers: string[];
  rows: any[][];
  total_rows: number;
  column_semantics?: MarketWatchColumnSemantics[];
  report_week?: string;
}

/** @deprecated Use fetchMarketWatchFilterOptions instead */
export async function fetchMarketWatchFilters(): Promise<MarketWatchFilters> {
  const response = await apiClient.get<MarketWatchFilters>('/market-watch/filters');
  return response.data;
}

export interface MarketWatchFilterOptionsResponse {
  field: string;
  values: string[];
  cached: boolean;
}

export async function fetchMarketWatchFilterOptions(
  field: "brand" | "target_asin" | "keyword",
  filters: { target_asin?: string[]; keyword?: string[]; brand?: string[] } = {}
): Promise<MarketWatchFilterOptionsResponse> {
  const params = new URLSearchParams();
  params.set("field", field);
  if (filters.target_asin && filters.target_asin.length > 0) {
    params.set("target_asin", filters.target_asin.join(","));
  }
  if (filters.keyword && filters.keyword.length > 0) {
    params.set("keyword", filters.keyword.join(","));
  }
  if (filters.brand && filters.brand.length > 0) {
    params.set("brand", filters.brand.join(","));
  }

  const response = await apiClient.get<MarketWatchFilterOptionsResponse>(
    `/market-watch/filters/options?${params.toString()}`
  );
  return response.data;
}

export async function fetchMarketWatchData(
  asins: string[],
  keywords: string[],
  brands: string[] = [],
  options?: {
    isExcludeSameBrand?: boolean;
    excludedBrands?: string[];
    isExcludeSpreetailAsin?: boolean;
  }
): Promise<MarketWatchDataResponse> {
  const params = new URLSearchParams();
  if (asins.length > 0) {
    params.set("asin", asins.join(","));
  }
  if (keywords.length > 0) {
    params.set("keyword", keywords.join(","));
  }
  if (brands.length > 0) {
    params.set("brand", brands.join(","));
  }
  if (options?.isExcludeSameBrand) {
    params.set("is_exclude_same_brand", "true");
  }
  if (options?.excludedBrands && options.excludedBrands.length > 0) {
    params.set("exclude_specific_brands", options.excludedBrands.join(","));
  }
  if (options?.isExcludeSpreetailAsin) {
    params.set("is_exclude_spreetail_asin", "true");
  }

  console.log("[MW-SPREETAIL] fetchMarketWatchData →", {
    isExcludeSpreetailAsin: options?.isExcludeSpreetailAsin ?? false,
    paramSent: params.has("is_exclude_spreetail_asin"),
    fullUrl: `/market-watch/data?${params.toString()}`,
  });

  const response = await apiClient.get<MarketWatchDataResponse>(
    `/market-watch/data?${params.toString()}`
  );
  return response.data;
}

// ============================================
// MARKET WATCH ANALYSIS API
// ============================================

export interface MarketWatchAnalyzeRequest {
  table_data: {
    headers: string[];
    rows: any[][];
  };
  use_dynamic_price_cluster?: boolean;
  price_variance_pct?: number;
  variance_growth_factor?: number;
  has_cluster_overrides?: boolean;
  is_enabled_seasonality?: boolean;
  is_exclude_same_brand: boolean;
  exclude_specific_brands: string[];
  report_week?: string;
}

export interface MarketWatchAnalyzeResponse {
  job_id: string;
}

export interface RuleMatch {
  target_asin?: string;
  global_price_cluster_label: string;
  global_cluster_feature_label?: string;
  demand_share_pct: number;
  forecast: number;
  additional_context?: Record<string, any>;
}

export interface BusinessRuleResult {
  rule_id: string;
  rule_name: string;
  triggered: boolean;
  matches: RuleMatch[];
  reasoning?: string;
}

export interface Recommendation {
  priority: number;
  rule_triggered: string;
  action: string;
  rationale: string;
  expected_impact?: string;
}

export interface PivotSummary {
  total_rows: number;
  price_clusters: number;
  feature_clusters: number;
}

export interface MarketWatchPivotConfig {
  rows: string[];
  columns: string[];
  values: { field: string; aggregation: string }[];
  calculated_columns?: { name: string; formula?: string; description: string }[];
  [key: string]: any;
}

export interface SeasonalEventRelevance {
  event: string;
  date: string;
  relevance: string;
}

export interface AsinSeasonality {
  is_seasonal: boolean;
  seasonality_type: string;
  confidence_score: number;
  next_peak_event: string | null;
  next_peak_date: string | null;
  weeks_until_next_peak: number | null;
  upcoming_events: SeasonalEventRelevance[];
  trend_next_3_weeks: {
    weeks: { week: number; date_range: string; events: string[]; direction: string; narrative: string }[];
    overall_direction: string;
    summary: string;
  } | null;
  trend_next_3_months: {
    months: { month: string; events: string[]; direction: string; narrative: string }[];
    overall_direction: string;
    summary: string;
  } | null;
  reasoning: string;
  curve_position?: string;
  forward_outlook?: string;
  seasonality_factor?: number;
}

export interface PerAsinAnalysis {
  target_asin: string;
  analysis_mode: string;
  rules_evaluated: BusinessRuleResult[];
  recommendations: Recommendation[];
  seasonality?: AsinSeasonality | null;
  /**
   * Pre-resolved seasonality note for the target ASIN. Null when no
   * material directional signal — frontend renders nothing in that case
   * (discipline rule: absence is itself meaningful).
   *
   * Includes the "Estimates adjusted for seasonality. " prefix used by the
   * JESS sheet. The header-annotation renderer in excelHelpers.ts strips
   * that prefix and adds its own "⚠ Seasonality: " visual marker.
   */
  seasonality_note?: string | null;
}

export interface MarketWatchAnalyzeResult {
  target_asins_analyzed: string[];
  pivot_config: MarketWatchPivotConfig;
  grouped_grid: Record<string, any>[];
  pivot_summary: PivotSummary;
  per_asin_analysis: PerAsinAnalysis[];
  excel_styled_report?: Record<string, any>;
  execution_time_ms: number;
  report_start_date?: string;
  report_end_date?: string;
  report_week?: string;
  updated_table_data?: { headers: string[]; rows: any[][] };
  seasonality_error?: string | null;
}

export async function startMarketWatchAnalysis(
  request: MarketWatchAnalyzeRequest
): Promise<MarketWatchAnalyzeResponse> {
  const response = await apiClient.post<MarketWatchAnalyzeResponse>(
    '/market-watch/analyze',
    request
  );
  return response.data;
}

// ============================================
// USER API MODELS & FUNCTIONS
// ============================================

export interface UserData {
  email: string;
  auth0_user_id: string;
  name: string;
}

// ---------------------------------------------------------------------------
// Market Watch Configuration (Feasibility Weights + Dynamic Cluster Settings)
// ---------------------------------------------------------------------------

export interface DynamicClusterSettings {
  price_variance_pct: number;
  variance_growth_factor: number | null;
}

export interface MarketWatchConfigResponse {
  feasibility_weights: Record<string, number>;
  is_default: boolean;
  dynamic_cluster_settings: DynamicClusterSettings;
  is_cluster_default: boolean;
}

export async function fetchFeasibilityWeights(): Promise<MarketWatchConfigResponse> {
  const response = await apiClient.get<MarketWatchConfigResponse>('/market-watch/config/settings');
  const data = response.data;
  console.log(
    "[MarketWatchConfig] Loaded:",
    data.is_default ? "DEFAULTS" : "CUSTOM",
    data.feasibility_weights,
    data.dynamic_cluster_settings
  );
  return data;
}

export async function saveFeasibilityWeights(
  weights?: Record<string, number>,
  dynamicClusterSettings?: DynamicClusterSettings
): Promise<MarketWatchConfigResponse> {
  const body: Record<string, unknown> = {};
  if (weights) {
    body.feasibility_weights = weights;
  }
  if (dynamicClusterSettings) {
    body.dynamic_cluster_settings = dynamicClusterSettings;
  }
  const response = await apiClient.put<MarketWatchConfigResponse>(
    '/market-watch/config/settings',
    body
  );
  const data = response.data;
  console.log("[MarketWatchConfig] Saved:", data.feasibility_weights, data.dynamic_cluster_settings);
  return data;
}

export async function resetFeasibilityWeights(): Promise<MarketWatchConfigResponse> {
  const response = await apiClient.delete<MarketWatchConfigResponse>('/market-watch/config/settings');
  const data = response.data;
  console.log("[MarketWatchConfig] Reset to defaults:", data.feasibility_weights, data.dynamic_cluster_settings);
  return data;
}

export interface UserResponse {
  _id: string;
  email: string;
  auth0_user_id: string;
  name: string;
  created_at?: string;
  updated_at?: string;
  last_login_at?: string;
}

export async function createOrUpdateUser(userData: UserData): Promise<UserResponse> {
  try {
    console.log("👤 Creating/updating user in database...", {
      email: userData.email,
      auth0_user_id: userData.auth0_user_id,
      name: userData.name,
    });

    const response = await apiClient.post<UserResponse>('/users', userData);
    console.log("✅ User data saved to database successfully");
    return response.data;
  } catch (error) {
    console.error("❌ Error creating/updating user:", error);
    throw error;
  }
}

// ============================================
// EXISTING TEMPLATE MODELS & FUNCTIONS
// ============================================

export interface CalculatedColumn {
  column_id: string;
  header: string;
  data_type: string;
  format: string;
  width: number;
  is_computed: boolean;
  formula: string;
  formula_type: string;
  description: string;
  order: number;
}

export interface SaveTemplateRequest {
  template_code: string;
  calculated_columns: CalculatedColumn[];
  template_name?: string;
}

export interface SaveTemplateResponse {
  message: string;
  template_id?: string;
  modified_count?: number;
  is_update?: boolean;
}

export const saveTemplate = async (
  templateData: SaveTemplateRequest
): Promise<SaveTemplateResponse> => {
  try {
    const response = await apiClient.post<SaveTemplateResponse>(
      '/templates/calculated_columns',
      templateData
    );
    return response.data;
  } catch (error) {
    console.error("Error saving template:", error);
    throw error;
  }
};

export const getUserTemplate = async (
  templateCode: string
): Promise<{
  exists: boolean;
  template_name?: string;
  template_id?: string;
  template_code?: string;
}> => {
  try {
    const response = await apiClient.get(`/templates/user_template/${templateCode}`);
    return response.data;
  } catch (error) {
    console.error("Error checking template:", error);
    throw error;
  }
};

export const getCurrentDashboardType = async (): Promise<string> => {
  try {
    return await Excel.run(async (context) => {
      const sheet = context.workbook.worksheets.getActiveWorksheet();
      sheet.load("name");
      await context.sync();

      const sheetName = sheet.name;
      console.log("Detecting dashboard type from sheet name:", sheetName);

      if (sheetName.startsWith("MW ")) {
        return "market_watch";
      } else if (
        sheetName.toLowerCase().includes("advertising") ||
        sheetName.toLowerCase().includes("marketing")
      ) {
        return "advertising";
      } else if (sheetName.toLowerCase().includes("sales")) {
        return "sales";
      } else if (sheetName.toLowerCase().includes("inventory")) {
        return "inventory";
      }

      console.log("Could not determine dashboard type, defaulting to 'advertising'");
      return "advertising";
    });
  } catch (error) {
    console.error("Error detecting dashboard type:", error);
    return "advertising";
  }
};

export const collectCurrentCalculatedColumns = async (): Promise<CalculatedColumn[]> => {
  try {
    return await Excel.run(async (context) => {
      const sheet = context.workbook.worksheets.getActiveWorksheet();
      const usedRange = sheet.getUsedRange();

      if (!usedRange) {
        console.log("No data found in the worksheet");
        return [];
      }

      // Load only metadata first to avoid payload size issues
      usedRange.load("columnCount, rowCount, address");

      // Load tables to get column names for structured reference conversion
      const tables = sheet.tables;
      tables.load("items");
      await context.sync();

      console.log("=== DEBUGGING TEMPLATE SAVE ===");
      console.log("Used range address:", usedRange.address);
      console.log("Column count:", usedRange.columnCount);
      console.log("Row count:", usedRange.rowCount);
      console.log("Tables found:", tables.items.length);

      const calculatedColumns: CalculatedColumn[] = [];

      // Dynamically detect header row from the Excel Table (MW Raw Data uses Row 5, advertising uses Row 4)
      let HEADER_ROW_INDEX: number = EXCEL_LAYOUT.HEADER_ROW_INDEX; // default 0-based (Row 4 in Excel)
      if (tables.items.length > 0) {
        const firstTable = tables.items[0];
        const tableHeaderRange = firstTable.getHeaderRowRange();
        tableHeaderRange.load("rowIndex");
        await context.sync();
        HEADER_ROW_INDEX = tableHeaderRange.rowIndex;
        console.log(
          "Header row detected from table:",
          HEADER_ROW_INDEX,
          `(Row ${HEADER_ROW_INDEX + 1} in Excel)`
        );
      }

      const DATA_START_ROW_INDEX = HEADER_ROW_INDEX + 1;
      const FORMULA_ROW_FOR_REFERENCE = DATA_START_ROW_INDEX + 1;

      // Convert to Excel column letter
      const toColLetter = (n: number) => {
        let num = Math.max(1, Math.floor(n));
        let name = "";
        while (num > 0) {
          const rem = (num - 1) % 26;
          name = String.fromCharCode(65 + rem) + name;
          num = Math.floor((num - 1) / 26);
        }
        return name;
      };

      // Load only header row and a small sample of data rows to detect formulas
      // This avoids loading the entire dataset which can exceed payload limits
      const SAMPLE_ROWS = Math.min(EXCEL_LAYOUT.SAMPLE_ROWS, usedRange.rowCount - DATA_START_ROW_INDEX); // Sample first N data rows
      const headerRowRange = sheet.getRange(
        `A${HEADER_ROW_INDEX + 1}:${toColLetter(usedRange.columnCount)}${HEADER_ROW_INDEX + 1}`
      );
      const sampleDataRange = sheet.getRange(
        `A${DATA_START_ROW_INDEX + 1}:${toColLetter(usedRange.columnCount)}${DATA_START_ROW_INDEX + SAMPLE_ROWS}`
      );

      headerRowRange.load("values");
      sampleDataRange.load("formulas, values");
      await context.sync();

      const headers = headerRowRange.values[0] || [];
      const sampleFormulas = sampleDataRange.formulas || [];
      const sampleValues = sampleDataRange.values || [];

      // Get all column names from the table (if exists) for structured reference conversion
      let tableColumnNames: string[] = headers.map((h) => (h ? h.toString().trim() : ""));
      let hasTable = false;

      // **CRITICAL FIX: Create table if it doesn't exist**
      if (tables.items.length === 0) {
        console.log("⚠️ No table found - creating table to enable structured references...");

        try {
          // Find the actual last column with data in header row
          let lastColWithData = 0;
          for (let i = headers.length - 1; i >= 0; i--) {
            if (
              headers[i] !== null &&
              headers[i] !== undefined &&
              String(headers[i]).trim() !== ""
            ) {
              lastColWithData = i + 1; // 1-based
              break;
            }
          }

          const lastColLetter = toColLetter(lastColWithData);
          const lastRow = usedRange.rowCount;

          // Create table over the data range (header row = row 4, which is index 3)
          const tableRange = sheet.getRange(`A${HEADER_ROW_INDEX + 1}:${lastColLetter}${lastRow}`);
          const newTable = sheet.tables.add(tableRange, true); // true = has headers
          newTable.name = `DataTable_${Date.now()}`;
          await context.sync();

          console.log(`✅ Created table: A${HEADER_ROW_INDEX + 1}:${lastColLetter}${lastRow}`);

          // Reload tables
          tables.load("items");
          await context.sync();

          // Get column names from newly created table
          const table = tables.items[0];
          table.columns.load("items/name");
          await context.sync();
          tableColumnNames = table.columns.items.map((col) => col.name);
          hasTable = true;

          console.log("✅ Table created with columns:", tableColumnNames);
        } catch (tableError) {
          console.error("❌ Failed to create table:", tableError);
          console.log("⚠️ Continuing without table - formulas will use cell references");
        }
      } else {
        const table = tables.items[0];
        table.columns.load("items/name");
        await context.sync();
        tableColumnNames = table.columns.items.map((col) => col.name);
        hasTable = true;
        console.log("✅ Table found with columns:", tableColumnNames);
      }

      console.log("Headers found (Row 4):", headers);
      console.log(`Sample formulas (from Row 5, ${SAMPLE_ROWS} rows):`, sampleFormulas.slice(0, 3));

      // Process columns one at a time to avoid payload issues
      for (let colIndex = 0; colIndex < usedRange.columnCount; colIndex++) {
        // Check if this column has formulas in the sample
        const columnSampleFormulas = sampleFormulas.map((row) => row[colIndex]);
        const hasFormulas = columnSampleFormulas.some((formula) => {
          const hasFormula = formula && formula.toString().trim().startsWith("=");
          if (hasFormula) {
            console.log(`  ✓ Found formula in column ${colIndex}: ${formula}`);
          }
          return hasFormula;
        });

        console.log(`\n--- Column ${colIndex} ---`);
        console.log(`Header (Row 4): "${headers[colIndex]}"`);
        console.log(`Has formulas in column ${colIndex}:`, hasFormulas);

        if (hasFormulas) {
          const headerValue = headers[colIndex];

          if (!headerValue || headerValue === "" || headerValue.toString().trim() === "") {
            console.log(`⚠️ Skipping column ${colIndex} - no header found in Row 4`);
            continue;
          }

          const header = headerValue.toString().trim();
          console.log(`✓ Adding calculated column: "${header}" at column index ${colIndex}`);

          // Get the first formula found in the sample
          let sampleFormula = columnSampleFormulas.find(
            (formula) => formula && formula.toString().trim().startsWith("=")
          );

          if (sampleFormula) {
            const originalFormula = sampleFormula.toString().trim();
            console.log(`📝 Original formula: ${originalFormula}`);

            // Convert to structured reference if we have a table
            if (hasTable && tableColumnNames.length > 0) {
              try {
                const structuredFormula = convertToStructuredReference(
                  originalFormula,
                  tableColumnNames,
                  FORMULA_ROW_FOR_REFERENCE
                );
                sampleFormula = structuredFormula;
                console.log(`✅ Converted to structured reference: ${structuredFormula}`);
              } catch (conversionError) {
                console.error(
                  `⚠️ Failed to convert formula to structured reference:`,
                  conversionError
                );
                console.log(`   Using original formula: ${originalFormula}`);
              }
            } else {
              console.log(
                `⚠️ No table found - saving formula as-is (may not work correctly on load)`
              );
            }
          }

          // Get sample values for data type and format detection
          const columnSampleValues = sampleValues.map((row) => row[colIndex]);
          const dataType = determineDataType(columnSampleValues);
          const format = determineFormat(columnSampleValues, header, sampleFormula);

          calculatedColumns.push({
            column_id: header.toLowerCase().replace(/[^a-z0-9]/g, "_"),
            header: header,
            data_type: dataType,
            format: format,
            width: 100,
            is_computed: true,
            formula: sampleFormula ? sampleFormula.toString().trim() : "",
            formula_type: "structured", // Changed from "cell" to "structured"
            description: `Calculated column: ${header}`,
            order: calculatedColumns.length + 1,
          });
        }
      }

      console.log(`\n=== SUMMARY ===`);
      console.log(`Total calculated columns found: ${calculatedColumns.length}`);
      console.log("Calculated columns:", JSON.stringify(calculatedColumns, null, 2));

      if (calculatedColumns.length === 0) {
        console.log("\n⚠️ WARNING: No calculated columns found!");
      }

      return calculatedColumns;
    });
  } catch (error) {
    console.error("Error collecting calculated columns:", error);
    throw new Error("Failed to collect current dashboard columns");
  }
};

function determineDataType(values: any[]): string {
  const numericValues = values.filter(
    (val) => val !== null && val !== undefined && val !== "" && !isNaN(Number(val))
  );

  if (numericValues.length > values.length * 0.5) {
    const hasPercentages = numericValues.some(
      (val) => typeof val === "string" && val.includes("%")
    );
    return hasPercentages ? "percentage" : "number";
  }

  return "text";
}

function determineFormat(values: any[], header?: string, formula?: string): string {
  const headerLower = header?.toLowerCase() || "";
  const formulaLower = formula?.toLowerCase() || "";

  const numericValues = values.filter(
    (val) => val !== null && val !== undefined && val !== "" && !isNaN(Number(val))
  );

  const percentageHeaders = ["ctr", "rate", "percent", "share", "roi", "roas"];
  if (percentageHeaders.some((h) => headerLower.includes(h))) {
    return "0.00%";
  }

  if (formulaLower.includes("%")) {
    return "0.00%";
  }

  if (/(ctr|rate|percent|share)/.test(formulaLower)) {
    return "0.00%";
  }

  if (numericValues.length > 0) {
    return "0.00";
  }

  return "General";
}

// ============================================
// SHARED INTERFACES FOR SMART MATCH
// ============================================

export interface TableData {
  table_name: string;
  headers: string[];
  rows: any[][];
}

// ============================================
// SMART MATCH / FUZZY SEARCH API
// ============================================

export interface GroundingTableRequest {
  left_table: TableData;
  right_table: TableData;
  matched_columns?: Array<{
    left_column: string;
    right_column: string;
  }>;
  user_intent?: string;
}

export interface GroundingTableResponse {
  grounded_table: string;
  reasoning: string;
}

// New interfaces for auto-setup generation
export interface AutoSetupRequest {
  left_table: TableData;
  right_table: TableData;
  user_intent?: string | null;
}

export interface AutoSetupResponse {
  grounded_table: string;
  reasoning: string;
  matching_intent: string;
  logic_operator: "AND" | "OR";
  match_groups: Array<
    Array<{
      left_column: string;
      right_column: string;
    }>
  >;
}

export interface FuzzySearchRequest {
  left_table: any[][];
  right_table: any[][];
  match_columns?: Array<{
    left_column: string;
    right_column: string;
  }>;
  threshold: number;
  scorer?: string;
  max_match?: number;
  logic_operator?: "AND" | "OR";
  match_groups?: Array<
    Array<{
      left_column: string;
      right_column: string;
    }>
  >;
  output_columns?: string[];
  user_intent?: string;
  matching_formulas?: string;
}

export interface FuzzySearchResponse {
  total_matches: number;
  threshold: number;
  scorer: string;
  logic_operator?: string;
  output_columns_applied?: boolean;
  returned_columns?: string[];
  is_successful?: boolean;
  reasoning?: string;
  attempts?: number;
  matching_formulas?: string;
  results: Array<{
    [key: `left_${string}`]: any;
    [key: `right_${string}`]: any;
    match_score: number;
    matched_on_left: string | null;
    matched_on_right: string | null;
    left_value: string | null;
    right_value: string | null;
  }>;
}

export interface FuzzySearchJobResponse {
  job_id: string;
}

export async function generateGroundingTable(
  request: GroundingTableRequest
): Promise<GroundingTableResponse> {
  try {
    console.log("Generating grounding table...");
    console.log("Left table:", request.left_table.table_name);
    console.log("Right table:", request.right_table.table_name);
    console.log("Matched columns:", request.matched_columns?.length || 0);
    console.log("User intent:", request.user_intent ? "Provided" : "Not provided");

    const response = await apiClient.post<GroundingTableResponse>(
      '/fuzzy_search/generate_grounding_table',
      request
    );
    const result = response.data;

    console.log("✅ Grounding table generated");
    console.log("   Grounded table:", result.grounded_table);
    console.log("   Reasoning:", result.reasoning);

    return result;
  } catch (error) {
    console.error("❌ Error generating grounding table:", error);
    throw error;
  }
}

// New function for auto-setup generation (Method 1 & 2)
export async function generateAutoSetup(request: AutoSetupRequest): Promise<AutoSetupResponse> {
  try {
    console.log("🤖 Generating auto setup...");
    console.log("Left table:", request.left_table.table_name);
    console.log("Right table:", request.right_table.table_name);
    console.log(
      "User intent:",
      request.user_intent ? "Provided (Method 2)" : "Not provided (Method 1)"
    );

    const response = await apiClient.post<AutoSetupResponse>(
      '/fuzzy_search/generate_auto_setup',
      request
    );
    const result = response.data;

    console.log("✅ Auto setup generated");
    console.log("   Grounded table:", result.grounded_table);
    console.log("   Matching intent:", result.matching_intent);
    console.log("   Logic operator:", result.logic_operator);
    console.log("   Match groups:", result.match_groups.length);

    return result;
  } catch (error) {
    console.error("❌ Error generating auto setup:", error);
    throw error;
  }
}

export async function performFuzzySearch(
  request: FuzzySearchRequest
): Promise<FuzzySearchJobResponse> {
  try {
    console.log("🔍 Performing fuzzy search...");
    console.log("   Left table rows:", request.left_table.length - 1);
    console.log("   Right table rows:", request.right_table.length - 1);
    console.log("   Logic operator:", request.logic_operator || "AND");

    if (request.logic_operator === "OR") {
      console.log("   Match groups:", request.match_groups?.length);
    } else {
      console.log("   Match columns:", request.match_columns?.length);
    }

    console.log("   Threshold:", request.threshold);

    if (request.user_intent) {
      console.log("   User Intent:", request.user_intent);
    }

    if (request.matching_formulas) {
      console.log("   Matching formulas: ✅ Cached (skipping LLM)");
    }

    const response = await apiClient.post<FuzzySearchJobResponse>('/fuzzy_search/', request);
    return response.data;
  } catch (error) {
    console.error("Error performing fuzzy search:", error);
    throw error;
  }
}

// ============================================
// PROMPT HISTORY API
// ============================================

export interface PromptHistoryItem {
  id: string;
  shortName: string;
  prompt: string;
  createdAt: number;
}

export interface PromptHistoryResponse {
  items: PromptHistoryItem[];
}

export interface SavePromptRequest {
  prompt: string;
  default_prompt?: string;
  shortName?: string;
}

export interface SavePromptResponse {
  message: string;
  id?: string;
}

export interface DeletePromptResponse {
  message: string;
}

export interface ClearPromptHistoryResponse {
  message: string;
  deleted_count: number;
}

// ============================
// Insights history (prompt + data + insights)
// ============================
export interface InsightsHistoryItem {
  id: string;
  prompt: string;
  default_prompt?: string;
  shortName: string;
  createdAt: number;
  table_data?: {
    headers: string[];
    rows: any[][];
  };
  insights?: any;
}

export interface InsightsHistoryResponse {
  items: InsightsHistoryItem[];
}

export interface SaveInsightsRunRequest {
  prompt: string;
  default_prompt: string;
  shortName: string;
  table_data: {
    headers: string[];
    rows: any[][];
  };
  insights: any;
  sheet_name?: string;
  range_address?: string;
}

/**
 * Save a prompt to the user's prompt history
 */
export async function savePromptToHistory(request: SavePromptRequest): Promise<SavePromptResponse> {
  try {
    const response = await apiClient.post<SavePromptResponse>('/prompt_history', request);
    return response.data;
  } catch (error) {
    console.error("Error saving prompt to history:", error);
    throw error;
  }
}

export async function saveInsightsRun(request: SaveInsightsRunRequest): Promise<void> {
  try {
    await apiClient.post('/insights_history', request);
  } catch (error) {
    console.error("Error saving insights run:", error);
    throw error;
  }
}

export async function getInsightsRun(runId: string): Promise<InsightsHistoryItem> {
  try {
    const response = await apiClient.get<InsightsHistoryItem>(
      `/insights_history/${encodeURIComponent(runId)}`
    );
    return response.data;
  } catch (error) {
    console.error("Error getting insights run:", error);
    throw error;
  }
}
/**
 * Get the user's insights history (prompt + data + insights runs)
 */
export async function getInsightsHistory(): Promise<InsightsHistoryResponse> {
  try {
    const response = await apiClient.get<InsightsHistoryResponse>('/insights_history');
    return response.data;
  } catch (error) {
    console.error("Error getting insights history:", error);
    throw error;
  }
}

/**
 * Get the user's prompt history
 */
export async function getPromptHistory(defaultPrompt?: string): Promise<PromptHistoryResponse> {
  try {
    const url = defaultPrompt
      ? `/prompt_history?default_prompt=${encodeURIComponent(defaultPrompt)}`
      : `/prompt_history`;

    const response = await apiClient.get<PromptHistoryResponse>(url);
    return response.data;
  } catch (error) {
    console.error("Error getting prompt history:", error);
    throw error;
  }
}

/**
 * Delete a specific prompt from history
 */
export async function deletePromptFromHistory(promptId: string): Promise<DeletePromptResponse> {
  try {
    const response = await apiClient.delete<DeletePromptResponse>(
      `/prompt_history/${promptId}`
    );
    return response.data;
  } catch (error) {
    console.error("Error deleting prompt from history:", error);
    throw error;
  }
}

/**
 * Clear all prompts from history (except default)
 */
export async function clearPromptHistory(
  defaultPrompt?: string
): Promise<ClearPromptHistoryResponse> {
  try {
    const url = defaultPrompt
      ? `/prompt_history?default_prompt=${encodeURIComponent(defaultPrompt)}`
      : `/prompt_history`;

    const response = await apiClient.delete<ClearPromptHistoryResponse>(url);
    return response.data;
  } catch (error) {
    console.error("Error clearing prompt history:", error);
    throw error;
  }
}
